import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { useState, useEffect, useCallback } from 'react';

export interface User {
  id: string;
  clerk_user_id: string;
  email: string;
  username?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  is_verified: boolean;
  role: string;
  subscription_tier: string;
  is_premium: boolean;
  is_subscription_active: boolean;
  created_at: string;
  updated_at: string;
  last_login_at?: string;
  login_count: number;
}

export interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  token: string | null;
  error: string | null;
}

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

export function useAuth() {
  const { isLoaded, isSignedIn, getToken, signOut } = useClerkAuth();
  const { user: clerkUser } = useUser();

  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    token: null,
    error: null,
  });

  // Función para obtener el token de Clerk
  const getClerkToken = useCallback(async () => {
    if (!isSignedIn) return null;

    try {
      const token = await getToken();
      return token;
    } catch (error) {
      console.error('❌ Error obteniendo token de Clerk:', error);
      return null;
    }
  }, [isSignedIn, getToken]);

  // Función para sincronizar con el backend
  const syncWithBackend = useCallback(async (token: string) => {
    try {
      console.log('🔄 Sincronizando usuario con backend...');

      const response = await fetch(`${API_BASE_URL}/auth/clerk/me`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('✅ Usuario sincronizado:', userData);

        setAuthState(prev => ({
          ...prev,
          user: userData,
          isAuthenticated: true,
          token,
          error: null,
          isLoading: false,
        }));

        return userData;
      } else {
        // Si el usuario no existe en el backend, intentar crearlo
        console.log('⚠️ Usuario no encontrado en backend, intentando crear...');

        const createResponse = await fetch(
          `${API_BASE_URL}/auth/clerk/verify-token`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (createResponse.ok) {
          console.log('✅ Usuario creado en backend');
          // Intentar obtener el usuario nuevamente
          return await syncWithBackend(token);
        } else {
          throw new Error('No se pudo crear el usuario en el backend');
        }
      }
    } catch (error) {
      console.error('❌ Error sincronizando con backend:', error);
      setAuthState(prev => ({
        ...prev,
        error:
          error instanceof Error ? error.message : 'Error de sincronización',
        isLoading: false,
      }));
      return null;
    }
  }, []);

  // Efecto principal para manejar el estado de autenticación
  useEffect(() => {
    if (!isLoaded) {
      return; // Clerk aún no ha cargado
    }

    if (!isSignedIn) {
      // Usuario no autenticado
      setAuthState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        token: null,
        error: null,
      });
      return;
    }

    // Usuario autenticado, obtener token y sincronizar
    const initializeAuth = async () => {
      try {
        const token = await getClerkToken();
        if (token) {
          await syncWithBackend(token);
        } else {
          throw new Error('No se pudo obtener el token de Clerk');
        }
      } catch (error) {
        console.error('❌ Error inicializando autenticación:', error);
        setAuthState(prev => ({
          ...prev,
          error:
            error instanceof Error ? error.message : 'Error de inicialización',
          isLoading: false,
        }));
      }
    };

    initializeAuth();
  }, [isLoaded, isSignedIn, getClerkToken, syncWithBackend]);

  // Función para hacer logout
  const logout = useCallback(async () => {
    try {
      console.log('🚪 Cerrando sesión...');

      // Limpiar estado local
      setAuthState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        token: null,
        error: null,
      });

      // Cerrar sesión en Clerk
      await signOut();

      console.log('✅ Sesión cerrada exitosamente');
    } catch (error) {
      console.error('❌ Error cerrando sesión:', error);
    }
  }, [signOut]);

  // Función para refrescar el token
  const refreshToken = useCallback(async () => {
    if (!isSignedIn) return null;

    try {
      const token = await getClerkToken();
      if (token && authState.user) {
        setAuthState(prev => ({
          ...prev,
          token,
        }));
      }
      return token;
    } catch (error) {
      console.error('❌ Error refrescando token:', error);
      return null;
    }
  }, [isSignedIn, getClerkToken, authState.user]);

  // Función para reautenticar (útil para recuperar de errores)
  const reauthenticate = useCallback(async () => {
    if (!isSignedIn) return;

    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const token = await getClerkToken();
      if (token) {
        await syncWithBackend(token);
      }
    } catch (error) {
      console.error('❌ Error reautenticando:', error);
      setAuthState(prev => ({
        ...prev,
        error:
          error instanceof Error ? error.message : 'Error de reautenticación',
        isLoading: false,
      }));
    }
  }, [isSignedIn, getClerkToken, syncWithBackend]);

  return {
    // Estado
    ...authState,
    isLoaded,
    clerkUser,

    // Funciones
    logout,
    refreshToken,
    reauthenticate,
    getToken: getClerkToken,
  };
}
